import React, { useState } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { TextNotes } from './notes/TextNotes'
import { BusinessCardCapture } from './notes/BusinessCardCapture'
import { VoiceRecorder } from './notes/VoiceRecorder'
import { HandwrittenNotes } from './notes/HandwrittenNotes'

interface NotesCaptureProps {
  meetingId: number
  meetingDate: string
  meetingTime: string
  location: string
  onFinish: () => void
  onNoteAdded?: () => void // Optional callback for immediate return after note
}

type NoteType = 'text' | 'business-card' | 'handwritten' | 'voice'

export const NotesCapture: React.FC<NotesCaptureProps> = ({
  meetingId,
  meetingDate,
  meetingTime,
  location,
  onFinish,
  onNoteAdded,
}) => {
  const [activeTab, setActiveTab] = useState<NoteType>('text')
  const [savedNotes, setSavedNotes] = useState<{
    text: number
    'business-card': number
    handwritten: number
    voice: number
  }>({
    text: 0,
    'business-card': 0,
    handwritten: 0,
    voice: 0,
  })

  const tabs: { id: NoteType; label: string; labelShort: string; icon: string; available: boolean }[] = [
    { id: 'text', label: 'Notatki tekstowe', labelShort: 'Tekstowe', icon: '📝', available: true },
    { id: 'business-card', label: 'Wizytówka', labelShort: 'Wizytówka', icon: '💳', available: true },
    { id: 'handwritten', label: 'Odręczne', labelShort: 'Odręczne', icon: '✍️', available: true },
    { id: 'voice', label: 'Głosowe', labelShort: 'Głosowe', icon: '🎤', available: true },
  ]

  const handleNoteComplete = (type: NoteType, _noteId: number) => {
    setSavedNotes((prev) => ({
      ...prev,
      [type]: prev[type] + 1,
    }))

    // If onNoteAdded callback exists, trigger automatic return
    if (onNoteAdded) {
      onNoteAdded()
    }
  }

  const totalNotes = Object.values(savedNotes).reduce((sum, count) => sum + count, 0)

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl sm:text-3xl font-bold truncate">Dodaj notatki ze spotkania</h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">
            {meetingDate} • {meetingTime} • {location}
          </p>
        </div>
        <Button onClick={onFinish} variant="outline" className="shrink-0 w-full sm:w-auto">
          Zakończ ({totalNotes})
        </Button>
      </div>

      {/* Tabs Navigation - Horizontal scrollable with better mobile support */}
      <div className="border-b border-gray-200 -mx-4 sm:mx-0">
        <nav
          className="flex overflow-x-auto overflow-y-hidden gap-0.5 sm:gap-2"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => tab.available && setActiveTab(tab.id)}
              disabled={!tab.available}
              className={`
                flex items-center gap-0.5 sm:gap-2 py-2.5 px-1.5 sm:px-4 border-b-3 font-medium text-xs sm:text-sm transition-all whitespace-nowrap flex-shrink-0
                ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : tab.available
                    ? 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50 active:bg-gray-100'
                    : 'border-transparent text-gray-300 cursor-not-allowed'
                }
              `}
              style={{ borderBottomWidth: activeTab === tab.id ? '3px' : '2px' }}
            >
              <span className="text-base sm:text-lg">{tab.icon}</span>
              <span className="font-medium">
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.labelShort}</span>
              </span>
              {savedNotes[tab.id] > 0 && (
                <span className="bg-primary text-white text-xs rounded-full px-1.5 py-0.5 font-semibold min-w-[20px] text-center">
                  {savedNotes[tab.id]}
                </span>
              )}
              {!tab.available && (
                <span className="text-[10px] text-gray-400">(wkrótce)</span>
              )}
            </button>
          ))}
        </nav>
      </div>
      <style>{`
        nav::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      {/* Active Tab Content */}
      <Card className="p-4 sm:p-6 md:p-8">
        {activeTab === 'text' && (
          <TextNotes
            meetingId={meetingId}
            meetingDate={meetingDate}
            meetingTime={meetingTime}
            location={location}
            onComplete={(noteId) => handleNoteComplete('text', noteId)}
          />
        )}

        {activeTab === 'business-card' && (
          <BusinessCardCapture
            meetingId={meetingId}
            meetingDate={meetingDate}
            meetingTime={meetingTime}
            location={location}
            onComplete={(noteId) => handleNoteComplete('business-card', noteId)}
          />
        )}

        {activeTab === 'handwritten' && (
          <HandwrittenNotes
            meetingId={meetingId}
            meetingDate={meetingDate}
            meetingTime={meetingTime}
            location={location}
            onComplete={(noteId) => handleNoteComplete('handwritten', noteId)}
          />
        )}

        {activeTab === 'voice' && (
          <VoiceRecorder
            meetingId={meetingId}
            meetingDate={meetingDate}
            meetingTime={meetingTime}
            location={location}
            onComplete={(noteId) => handleNoteComplete('voice', noteId)}
          />
        )}
      </Card>

      {/* Summary Sidebar (Future) */}
      {totalNotes > 0 && (
        <Card className="p-4 bg-green-50 border-green-200">
          <p className="text-sm text-green-800">
            ✓ Zapisano {totalNotes} {totalNotes === 1 ? 'notatkę' : 'notatek'}
          </p>
        </Card>
      )}
    </div>
  )
}
